import BaseComponent from '../../BaseComponent';
import Cropper from 'cropperjs';

type PreviewMode = 'photo' | 'preset' | 'default';

const DEFAULT_PRESET_ID = 'default';

export default class UploadAvatarModal extends BaseComponent {

    protected cropper: Cropper | null = null;
    protected $form!: JQuery<HTMLFormElement>;

    protected $fileInput!: JQuery<HTMLInputElement>;
    protected $presetInput!: JQuery<HTMLInputElement>;
    protected $clearUploadedInput!: JQuery<HTMLInputElement>;

    protected $currentPreview!: JQuery<HTMLImageElement>;
    protected $uploadedThumb!: JQuery;
    protected $uploadedThumbImage!: JQuery<HTMLImageElement>;
    protected $cropperPane!: JQuery;
    protected $cropperImage!: JQuery<HTMLImageElement>;
    protected $grid!: JQuery;
    protected $bodyExceptCropper!: JQuery;

    /** Lookup of grid tiles by their preset id (avatar-1..N or 'default'). Built once at init. */
    protected presetTiles: Map<string, JQuery> = new Map();

    protected initialAvatarUrl = '';
    protected defaultAvatarUrl = '';
    protected initialUploadedPhotoUrl: string | null = null;
    protected initialPreset: string | null = null;
    protected initialHasUploadedPhoto = false;

    protected pendingPhotoBlobUrl: string | null = null;
    protected pendingPhotoFile: File | null = null;
    protected pendingCroppedPreviewUrl: string | null = null;
    protected pendingClearUploaded = false;
    protected selectedPreset: string | null = null;
    protected previewMode: PreviewMode = 'default';

    init(): void {
        this.$form = this.$component.find('form').first() as JQuery<HTMLFormElement>;

        this.$fileInput = this.$form.find('[data-cropper-input-image]') as JQuery<HTMLInputElement>;
        this.$presetInput = this.$form.find('[data-avatar-preset-input]') as JQuery<HTMLInputElement>;
        this.$clearUploadedInput = this.$form.find('[data-avatar-clear-uploaded]') as JQuery<HTMLInputElement>;

        this.$currentPreview = this.$component.find('[data-avatar-current-preview]') as JQuery<HTMLImageElement>;
        this.$uploadedThumb = this.$component.find('[data-avatar-uploaded-thumb]');
        this.$uploadedThumbImage = this.$component.find('[data-avatar-uploaded-image]') as JQuery<HTMLImageElement>;
        this.$cropperPane = this.$component.find('[data-avatar-cropper-pane]');
        this.$cropperImage = this.$component.find('[data-cropper-image]') as JQuery<HTMLImageElement>;
        this.$grid = this.$component.find('[data-avatar-grid]');
        this.$bodyExceptCropper = this.$component.find('.preview-row, [data-avatar-grid]');

        this.initialAvatarUrl = String(this.$component.attr('data-current-avatar-url') || '');
        this.defaultAvatarUrl = String(this.$component.attr('data-default-avatar-url') || '');
        const uploadedUrl = String(this.$component.attr('data-uploaded-photo-url') || '');
        this.initialUploadedPhotoUrl = uploadedUrl !== '' ? uploadedUrl : null;
        const currentPreset = String(this.$component.attr('data-current-preset') || '');
        this.initialPreset = currentPreset !== '' ? currentPreset : null;
        this.initialHasUploadedPhoto = String(this.$component.attr('data-has-uploaded-photo') || '0') === '1';

        this.$grid.find('[data-avatar-preset]').each((_, el) => {
            const id = el.getAttribute('data-avatar-preset') ?? '';
            this.presetTiles.set(id, $(el));
        });

        this.resetState();

        this.bindEvents();
    }

    private bindEvents(): void {
        this.$component.on('show.bs.modal', () => this.resetState());
        this.$component.on('hide.bs.modal', () => this.onHideModal());

        this.$component.on('click', '[data-avatar-upload-trigger]', (e) => {
            e.preventDefault();
            this.$fileInput.trigger('click');
        });

        this.$fileInput.on('change', (e) => this.onFilePicked(e));

        this.$component.on('click', '[data-avatar-uploaded-remove]', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.markUploadedForRemoval();
        });

        this.$component.on('click', '[data-avatar-uploaded-select]', (e) => {
            e.preventDefault();
            this.selectUploadedPhoto();
        });

        this.$component.on('click', '[data-avatar-preset]', (e) => {
            e.preventDefault();
            const preset = String($(e.currentTarget).attr('data-avatar-preset') || '');
            this.selectPreset(preset);
        });

        this.$component.on('click', '[data-avatar-crop-confirm]', (e) => {
            e.preventDefault();
            this.confirmCrop();
        });

        this.$component.on('click', '[data-avatar-crop-cancel]', (e) => {
            e.preventDefault();
            this.cancelCrop();
        });
    }

    private resetState(): void {
        this.destroyCropper();
        this.revokePendingBlob();

        this.pendingPhotoFile = null;
        this.pendingCroppedPreviewUrl = null;
        this.pendingClearUploaded = false;
        this.selectedPreset = this.initialPreset;

        if (this.initialPreset === DEFAULT_PRESET_ID) {
            this.previewMode = 'default';
        } else if (this.initialPreset !== null) {
            this.previewMode = 'preset';
        } else if (this.initialHasUploadedPhoto) {
            this.previewMode = 'photo';
        } else {
            this.previewMode = 'default';
        }

        this.$presetInput.val(this.initialPreset ?? '');
        this.$clearUploadedInput.val('0');
        this.$form.find('input[name="x"]').val('');
        this.$form.find('input[name="y"]').val('');
        this.$form.find('input[name="w"]').val('');
        this.$form.find('input[name="h"]').val('');
        (this.$fileInput[0] as HTMLInputElement).value = '';

        this.closeCropperPane();

        this.updatePreviewImage();
        this.updateUploadedThumb();
        this.updateActiveItem();
    }

    private updatePreviewImage(): void {
        this.$currentPreview.attr('src', this.resolvePreviewSrc());
    }

    private resolvePreviewSrc(): string {
        switch (this.previewMode) {
            case 'photo':
                return this.pendingCroppedPreviewUrl
                    ?? this.pendingPhotoBlobUrl
                    ?? (this.initialUploadedPhotoUrl+Math.random())
                    ?? this.initialAvatarUrl;
            case 'preset':
                return this.selectedPreset !== null
                    ? this.resolvePresetUrl(this.selectedPreset)
                    : this.initialAvatarUrl;
            case 'default':
                return this.getDefaultPlaceholderSrc();
        }
    }

    private updateUploadedThumb(): void {
        // Thumb is visible whenever there is any photo (initial uploaded or pending blob) — even if marked for removal,
        // so the user can re-select it before saving.
        const visiblePhotoUrl = this.pendingCroppedPreviewUrl
            ?? this.pendingPhotoBlobUrl
            ?? (this.initialUploadedPhotoUrl !== null ? (this.initialUploadedPhotoUrl+Math.random()) : null);

        if (visiblePhotoUrl !== null) {
            this.$uploadedThumbImage.attr('src', visiblePhotoUrl);
            this.$uploadedThumb.show();
        } else {
            this.$uploadedThumb.hide();
        }

        this.$uploadedThumb.toggleClass('is-dimmed', this.pendingClearUploaded);
    }

    private updateActiveItem(): void {
        this.presetTiles.forEach($tile => $tile.removeClass('is-active'));
        this.$uploadedThumb.removeClass('is-active');

        if (this.selectedPreset !== null && (this.previewMode === 'preset' || this.previewMode === 'default')) {
            this.presetTiles.get(this.selectedPreset)?.addClass('is-active');
        } else if (this.previewMode === 'photo') {
            this.$uploadedThumb.addClass('is-active');
        }
    }

    private selectPreset(preset: string): void {
        // If there is a pending in-flight upload (file picked but cropper still open) → discard it.
        if (this.cropper) {
            this.cancelCrop();
        }

        // Picking any tile clears the X-marked "remove photo" intent — only the X badge keeps that flag.
        this.pendingClearUploaded = false;
        this.$clearUploadedInput.val('0');

        if (preset === DEFAULT_PRESET_ID) {
            // Default-sentinel tile: store the sentinel so the resolver renders the theme default
            // even though an uploaded photo may still exist on disk.
            this.selectedPreset = DEFAULT_PRESET_ID;
            this.previewMode = 'default';
            this.$presetInput.val(DEFAULT_PRESET_ID);
        } else {
            this.selectedPreset = preset;
            this.previewMode = 'preset';
            this.$presetInput.val(preset);
        }

        this.updatePreviewImage();
        this.updateUploadedThumb();
        this.updateActiveItem();
    }

    private selectUploadedPhoto(): void {
        // Click on existing uploaded thumbnail → revert preview to that photo, drop preset selection.
        // Cancel any pending clearUploaded intent so the photo is kept on save.
        this.pendingClearUploaded = false;
        this.$clearUploadedInput.val('0');
        this.selectedPreset = null;
        this.$presetInput.val('');
        this.previewMode = 'photo';

        this.updatePreviewImage();
        this.updateUploadedThumb();
        this.updateActiveItem();
    }

    private onFilePicked(e: JQuery.ChangeEvent): void {
        const input = e.currentTarget as HTMLInputElement;
        const files = input.files;
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];

        if (!/^image\/\w+$/.test(file.type)) {
            input.value = '';
            window.alert(this.$fileInput.data('cropper-file-type-error') || 'Invalid file type.');
            return;
        }

        this.revokePendingBlob();
        this.pendingPhotoFile = file;
        this.pendingPhotoBlobUrl = URL.createObjectURL(file);

        this.openCropper(this.pendingPhotoBlobUrl);
    }

    private openCropper(blobUrl: string): void {
        this.destroyCropper();

        this.$bodyExceptCropper.hide();
        this.$cropperPane.show();
        this.$component.find('.modal-footer').addClass('d-none');

        this.$cropperImage.attr('src', blobUrl);

        this.cropper = new Cropper(this.$cropperImage[0] as HTMLImageElement, {
            aspectRatio: this.$cropperImage.data('aspect-ratio'),
            viewMode: 1,
            dragMode: 'move',
            toggleDragModeOnDblclick: false,
            minContainerHeight: 300,
            minCropBoxWidth: 128,
            minCropBoxHeight: 128,
            crop: (event) => this.onCrop(event),
        });
    }

    private confirmCrop(): void {
        if (!this.cropper || !this.pendingPhotoFile || !this.pendingPhotoBlobUrl) {
            this.cancelCrop();
            return;
        }

        // Snapshot cropped result for preview/thumb. The original file (uncropped) stays attached
        // to the file input — backend re-crops using the submitted x/y/w/h coordinates.
        try {
            const canvas = this.cropper.getCroppedCanvas({ width: 256, height: 256 });
            if (canvas) {
                this.pendingCroppedPreviewUrl = canvas.toDataURL('image/png');
            }
        } catch {
            // Fallback: if canvas export fails (e.g. CORS), keep the uncropped blob URL.
            this.pendingCroppedPreviewUrl = null;
        }

        // Pending photo accepted → preview mode = photo, preset cleared
        this.selectedPreset = null;
        this.$presetInput.val('');
        this.previewMode = 'photo';
        // Pending upload supersedes any "clearUploaded" intent.
        this.pendingClearUploaded = false;
        this.$clearUploadedInput.val('0');

        this.destroyCropper();
        this.closeCropperPane();

        // Re-attach the file to the form input so it's submitted.
        this.attachPendingFileToInput();

        this.updatePreviewImage();
        this.updateUploadedThumb();
        this.updateActiveItem();
    }

    private attachPendingFileToInput(): void {
        if (!this.pendingPhotoFile || typeof DataTransfer === 'undefined') {
            return;
        }

        const dt = new DataTransfer();
        dt.items.add(this.pendingPhotoFile);
        (this.$fileInput[0] as HTMLInputElement).files = dt.files;
    }

    private cancelCrop(): void {
        this.destroyCropper();
        this.revokePendingBlob();
        this.pendingPhotoFile = null;
        this.pendingCroppedPreviewUrl = null;
        (this.$fileInput[0] as HTMLInputElement).value = '';

        this.closeCropperPane();

        this.updatePreviewImage();
        this.updateUploadedThumb();
    }

    private markUploadedForRemoval(): void {
        // Cancel any pending upload (so X reverts to "no photo" state).
        if (this.pendingPhotoBlobUrl) {
            this.revokePendingBlob();
            this.pendingPhotoFile = null;
            this.pendingCroppedPreviewUrl = null;
            (this.$fileInput[0] as HTMLInputElement).value = '';
        }

        this.pendingClearUploaded = true;
        this.$clearUploadedInput.val('1');

        // If preview was the photo, fall back to either preset or default.
        if (this.previewMode === 'photo') {
            this.previewMode = this.selectedPreset !== null ? 'preset' : 'default';
        }

        this.updatePreviewImage();
        this.updateUploadedThumb();
        this.updateActiveItem();
    }

    private onCrop(e: Cropper.CropEvent): void {
        this.$form.find('input[name="x"]').val(e.detail.x);
        this.$form.find('input[name="y"]').val(e.detail.y);
        this.$form.find('input[name="w"]').val(e.detail.width);
        this.$form.find('input[name="h"]').val(e.detail.height);
    }

    private destroyCropper(): void {
        if (this.cropper) {
            this.cropper.destroy();
            this.cropper = null;
        }
    }

    private closeCropperPane(): void {
        this.$cropperPane.hide();
        this.$bodyExceptCropper.show();
        this.$component.find('.modal-footer').removeClass('d-none');
    }

    private revokePendingBlob(): void {
        if (this.pendingPhotoBlobUrl) {
            URL.revokeObjectURL(this.pendingPhotoBlobUrl);
            this.pendingPhotoBlobUrl = null;
        }
    }

    private onHideModal(): void {
        // Modal closed without saving → discard pending state.
        this.destroyCropper();
        this.revokePendingBlob();
    }

    private resolvePresetUrl(preset: string): string {
        const $tile = this.presetTiles.get(preset);
        if (!$tile) {
            return '';
        }
        return $tile.find('img').attr('src') || '';
    }

    private getDefaultPlaceholderSrc(): string {
        return this.defaultAvatarUrl !== '' ? this.defaultAvatarUrl : this.initialAvatarUrl;
    }

}
